import { useModalDialog } from "../hooks/useModalDialog";
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import HlcFooter from '../components/HlcFooter';
import { getCutoutImage } from '../utils/getCutoutImage';
import { categoryDisplayName } from '../utils/categoryDisplayName';
import { API_BASE_URL } from '../config/apiConfig';
import { cssVars } from '../theme';
import { addToCart, incrementQty, decrementQty } from '../redux/cartSlice';
import ProductCard from '../components/shop/ProductCard';
import QuickViewModal from '../components/shop/QuickViewModal';
import { FilterIcon, ChevronIcon, CloseIcon, ShieldIcon, TruckIcon } from '../components/shop/ShopIcons';
import '../styles/hlcShopSystem.css';

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'name-asc', label: 'Name: A-Z' },
];

const EnergyLeafIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 3C7 5 5 8 5 12a7 7 0 0 0 14 0c0-1.5-.5-3-1.5-4.5C16 9 14 9 13 7c-.8-1.5-1-2.7-2-4z" /></svg>
);
const HeadsetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 13a8 8 0 0 1 16 0" /><path d="M4 13v4a2 2 0 0 0 2 2h1v-6H5a1 1 0 0 0-1 1z" /><path d="M20 13v4a2 2 0 0 1-2 2h-1v-6h1a1 1 0 0 1 2 1z" /></svg>
);

export default function ProductByCategories() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const getCartItem = (id) => cartItems.find((item) => item.id === id);

  const { state } = location || {};
  const selectedCategory = state?.category || '';
  // Passed by MainCategory.js when a subcategory is chosen in its filter
  // (matches product.subcategory exactly, confirmed against live API data).
  const selectedSubcategory = state?.subcategory || '';

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterProduct, setFilterProduct] = useState('');
  const [filterWatt, setFilterWatt] = useState('');
  const [filterColour, setFilterColour] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [sortBy, setSortBy] = useState('featured');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { dialogRef, isModal } = useModalDialog(mobileFiltersOpen, () => setMobileFiltersOpen(false), "(max-width: 991px)");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  /* ================= FETCH: categories (for hero description + nav) and
     products (existing endpoint/lookup, unchanged) ================= */
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/public/categories/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error('Failed to fetch categories:', err));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch products:', err);
        if (!cancelled) {
          setProducts([]);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const categoryProducts = useMemo(
    () =>
      products.filter(
        (p) => p.category === selectedCategory && (!selectedSubcategory || p.subcategory === selectedSubcategory)
      ),
    [products, selectedCategory, selectedSubcategory]
  );

  const categoryObj = categories.find((c) => c.title === selectedCategory);
  // subcategories comes back from the API as an object keyed by ID
  // (e.g. { "27": { id, title, description, image } }), not an array.
  const subcategoryList = Object.values(categoryObj?.subcategories || {});
  const collectionName = categoryDisplayName(selectedCategory);
  const shortLabel = collectionName.replace(/ Collection$/, '');
  // Use the real fetched category object (carries the real .image field)
  // once it's loaded; the synthetic {title, category} object is only a
  // placeholder for the brief window before categories has loaded, so
  // getCutoutImage still has something to keyword-match against.
  const heroImage = getCutoutImage(categoryObj || { title: selectedCategory, category: selectedCategory });

  /* ================= REAL, INDEPENDENT FILTER OPTIONS — sourced only
     from this category's actual products, nothing invented ================= */
  const productList = useMemo(
    () => [...new Set(categoryProducts.map((p) => p.name).filter(Boolean))].sort(),
    [categoryProducts]
  );
  const wattList = useMemo(
    () => [...new Set(categoryProducts.flatMap((p) => (Array.isArray(p.wattColourModel) ? p.wattColourModel : [])))].sort(),
    [categoryProducts]
  );
  const colourList = useMemo(
    () => [...new Set(categoryProducts.flatMap((p) => (Array.isArray(p.colourType) ? p.colourType : [])).map((c) => c.trim()))].filter(Boolean).sort(),
    [categoryProducts]
  );

  useEffect(() => {
    setFilteredProducts(categoryProducts);
    setFilterProduct('');
    setFilterWatt('');
    setFilterColour('');
  }, [categoryProducts]);

  const applyFilters = () => {
    let temp = [...categoryProducts];
    if (filterProduct) temp = temp.filter((p) => p.name === filterProduct);
    if (filterWatt) {
      temp = temp.filter(
        (p) => Array.isArray(p.wattColourModel) && p.wattColourModel.some((w) => String(w).trim().toLowerCase() === filterWatt.trim().toLowerCase())
      );
    }
    if (filterColour) {
      temp = temp.filter(
        (p) => Array.isArray(p.colourType) && p.colourType.some((c) => String(c).trim().toLowerCase() === filterColour.trim().toLowerCase())
      );
    }
    setFilteredProducts(temp);
    setMobileFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilterProduct('');
    setFilterWatt('');
    setFilterColour('');
    setFilteredProducts(categoryProducts);
  };

  const activeFilterCount = [filterProduct, filterWatt, filterColour].filter(Boolean).length;

  const sortedProducts = useMemo(() => {
    const arr = [...filteredProducts];
    const priceOf = (p) => Number(p.discountedPrice || p.price) || 0;
    switch (sortBy) {
      case 'price-asc': return arr.sort((a, b) => priceOf(a) - priceOf(b));
      case 'price-desc': return arr.sort((a, b) => priceOf(b) - priceOf(a));
      case 'name-asc': return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      default: return arr;
    }
  }, [filteredProducts, sortBy]);

  const viewProduct = (id) => navigate(`/product/${id}`);
  const openModal = (product) => { setSelectedProduct(product); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedProduct(null); };

  const goToCategory = (title) => {
    const cat = categories.find((c) => c.title === title);
    navigate('/product-by-categories', { state: { category: title, subcategories: cat?.subcategories || [] } });
  };

  const filterPanel = (
    <>
      <div className="hlc-shop__filters-head">
        <h3>
          Filters
          {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
        </h3>
        <button type="button" className="hlc-shop__filters-close" onClick={() => setMobileFiltersOpen(false)} aria-label="Close filters">
          <CloseIcon />
        </button>
      </div>

      <label htmlFor="pbc_product">Product</label>
      <div className="hlc-shop__select-wrap">
        <select id="pbc_product" value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)} className={filterProduct ? 'has-value' : ''}>
          <option value="">All Products</option>
          {productList.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <ChevronIcon />
      </div>

      <label htmlFor="pbc_watt">Wattage / Model</label>
      <div className="hlc-shop__select-wrap">
        <select id="pbc_watt" value={filterWatt} onChange={(e) => setFilterWatt(e.target.value)} className={filterWatt ? 'has-value' : ''}>
          <option value="">All Wattages</option>
          {wattList.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <ChevronIcon />
      </div>

      {colourList.length > 0 && (
        <>
          <label htmlFor="pbc_colour">Colour</label>
          <div className="hlc-shop__select-wrap">
            <select id="pbc_colour" value={filterColour} onChange={(e) => setFilterColour(e.target.value)} className={filterColour ? 'has-value' : ''}>
              <option value="">All Colours</option>
              {colourList.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronIcon />
          </div>
        </>
      )}

      <div className="filter-actions">
        <button type="button" id="applyFilters" onClick={applyFilters}>Apply Filters</button>
        <button type="button" id="clearFilters" onClick={clearFilters}>Clear All</button>
      </div>
    </>
  );

  return (
    <>
      <style>{`
        #hlc-pbc-page, #hlc-pbc-page * { box-sizing: border-box; }

        .hlc-pbc__crumb {
          width: min(1360px, 94vw); margin: 0 auto;
          padding-top: clamp(22px, 3vw, 34px);
          font-size: 12px; color: var(--hlc-text-faint);
        }
        .hlc-pbc__crumb a { color: var(--hlc-text-secondary); text-decoration: none; }
        .hlc-pbc__crumb a:hover { color: var(--hlc-magenta-light); }
        .hlc-pbc__crumb span { margin: 0 6px; }

        .hlc-pbc__hero {
          width: min(1360px, 94vw); margin: 10px auto 0;
          display: grid; grid-template-columns: minmax(0, 1fr) 240px;
          gap: 32px; align-items: center;
          padding: clamp(14px, 2vw, 20px) 0 clamp(16px, 2vw, 22px);
        }
        .hlc-pbc__eyebrow {
          display: block; margin: 0 0 10px;
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.22em; text-transform: uppercase;
          color: var(--hlc-magenta-light);
        }
        .hlc-pbc__hero-info h1 {
          margin: 0 0 10px; font-size: clamp(26px, 3.4vw, 38px); font-weight: 800; letter-spacing: -0.01em; line-height: 1.15;
          color: #fff;
        }
        .hlc-pbc__hero-info p {
          margin: 0 0 18px; max-width: 520px; font-size: 14px; line-height: 1.6; color: var(--hlc-text-secondary);
        }
        .hlc-pbc__meta {
          display: flex; flex-wrap: wrap; gap: 10px;
        }
        .hlc-pbc__meta span {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 8px 14px; font-size: 12px; font-weight: 600; color: var(--hlc-text-secondary);
          background: var(--hlc-surface); border: 1px solid var(--hlc-border); border-radius: 999px;
        }
        .hlc-pbc__meta svg { width: 14px; height: 14px; color: var(--hlc-magenta-light); }

        .hlc-pbc__hero-visual {
          position: relative;
          aspect-ratio: 1 / 1;
          border-radius: var(--hlc-radius);
          background: radial-gradient(circle at 50% 45%, rgba(155,31,108,0.22), transparent 68%);
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .hlc-pbc__hero-visual img { width: 78%; height: 78%; object-fit: contain; }

        .hlc-pbc__catnav {
          width: min(1360px, 94vw); margin: 0 auto 8px;
          display: flex; gap: 8px; overflow-x: auto; padding-bottom: 6px;
          scrollbar-width: thin;
          scrollbar-color: var(--hlc-magenta-light) var(--hlc-bg-secondary);
        }
        @media (max-width: 767px) {
          .hlc-pbc__catnav { flex-wrap: wrap; overflow-x: visible; }
          #hlc-pbc-page .hlc-pbc__catnav button { max-width: 100%; white-space: normal; }
        }
        .hlc-pbc__catnav button {
          flex-shrink: 0;
          white-space: nowrap;
          padding: 9px 18px; font-size: 12px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
          background: var(--hlc-surface); border: 1px solid var(--hlc-border); border-radius: 999px;
          color: var(--hlc-text-secondary); cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
        }
        .hlc-pbc__catnav button:hover { border-color: rgba(155,31,108,0.4); color: #fff; }
        .hlc-pbc__catnav button.is-active {
          border-color: var(--hlc-magenta-light); background: rgba(155,31,108,0.14); color: var(--hlc-magenta-light);
          box-shadow: 0 0 0 1px rgba(155,31,108,0.25);
        }

        .hlc-pbc__subcats {
          width: min(1360px, 94vw); margin: 4px auto 28px;
        }
        .hlc-pbc__subcats-title {
          margin: 0 0 14px;
          font-size: 11px; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase;
          color: var(--hlc-text-faint);
        }
        .hlc-pbc__subcats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 16px;
        }
        .hlc-pbc__subcat-card {
          display: flex; flex-direction: column;
          background: var(--hlc-surface); border: 1px solid var(--hlc-border); border-radius: var(--hlc-radius-sm);
          overflow: hidden;
          transition: border-color 0.2s ease, transform 0.2s ease;
        }
        .hlc-pbc__subcat-card:hover {
          border-color: rgba(155,31,108,0.4); transform: translateY(-2px);
        }
        .hlc-pbc__subcat-media {
          position: relative;
          aspect-ratio: 1 / 1;
          background: radial-gradient(circle at 50% 45%, rgba(155,31,108,0.18), transparent 70%);
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .hlc-pbc__subcat-media img { width: 70%; height: 70%; object-fit: contain; }
        .hlc-pbc__subcat-body { padding: 12px 14px 16px; }
        .hlc-pbc__subcat-body h3 {
          margin: 0 0 4px; font-size: 13.5px; font-weight: 700; color: #fff; letter-spacing: -0.01em;
        }
        .hlc-pbc__subcat-body p {
          margin: 0; font-size: 12px; line-height: 1.5; color: var(--hlc-text-secondary);
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
        }

        @media (max-width: 900px) {
          .hlc-pbc__hero { grid-template-columns: 1fr; }
          .hlc-pbc__hero-visual { max-width: 260px; margin: 0 auto; aspect-ratio: 1/1; }
        }
      `}</style>

      <Navbar />
      <div style={{ marginTop: "var(--nav-h, 74px)" }} />

      <div id="hlc-pbc-page" className="hlc-shop-system" style={cssVars}>
        <div className="hlc-pbc__crumb">
          <Link to="/">Home</Link> <span>›</span> <Link to="/categories">Categories</Link> <span>›</span> <span>{shortLabel}</span>
        </div>

        <section className="hlc-pbc__hero">
          <div className="hlc-pbc__hero-info">
            <span className="hlc-pbc__eyebrow">HLC Lighting / Product Collection</span>
            <h1>{collectionName || 'Product Collection'}</h1>
            {categoryObj?.description && <p>{categoryObj.description}</p>}

            <div className="hlc-pbc__meta">
              <span><EnergyLeafIcon /> Energy Efficient</span>
              <span><ShieldIcon /> 1-Year Warranty</span>
              <span><TruckIcon /> Nationwide Delivery</span>
            </div>
          </div>

          <div className="hlc-pbc__hero-visual">
            {heroImage && <img src={heroImage} alt={selectedCategory} />}
          </div>
        </section>

        {categories.length > 0 && (
          <nav className="hlc-pbc__catnav" aria-label="Browse other categories">
            <button
              type="button"
              className={!selectedCategory ? 'is-active' : ''}
              onClick={() => navigate('/shop')}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={c.title === selectedCategory ? 'is-active' : ''}
                onClick={() => goToCategory(c.title)}
              >
                {categoryDisplayName(c.title).replace(/ Collection$/, '')}
              </button>
            ))}
          </nav>
        )}

        {subcategoryList.length > 0 && (
          <section className="hlc-pbc__subcats" aria-label="Subcategories in this collection">
            <h2 className="hlc-pbc__subcats-title">Shop by Subcategory</h2>
            <div className="hlc-pbc__subcats-grid">
              {subcategoryList.map((sub) => {
                const subImage = getCutoutImage(sub);
                return (
                  <div className="hlc-pbc__subcat-card" key={sub.id}>
                    <div className="hlc-pbc__subcat-media">
                      {subImage && <img src={subImage} alt={sub.title} />}
                    </div>
                    <div className="hlc-pbc__subcat-body">
                      <h3>{sub.title}</h3>
                      {sub.description && <p>{sub.description}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="hlc-shop__toolbar">
          <p className="hlc-shop__count">
            <b>{shortLabel}</b> — <b>{sortedProducts.length}</b> product{sortedProducts.length !== 1 ? 's' : ''}
          </p>

          <div className="hlc-shop__toolbar-right">
            <button type="button" className="hlc-shop__mobile-filters-btn" aria-expanded={mobileFiltersOpen} aria-controls="hlc-product-filters" onClick={() => setMobileFiltersOpen(true)}>
              <FilterIcon /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>

            <div className="hlc-shop__sort">
              <label htmlFor="pbc_sort">Sort by</label>
              <div className="hlc-shop__sort-wrap">
                <select id="pbc_sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <ChevronIcon />
              </div>
            </div>
          </div>
        </div>

        <div className="hlc-shop__inner">
          <div className={`hlc-shop__filters-scrim${mobileFiltersOpen ? ' is-open' : ''}`} onClick={() => setMobileFiltersOpen(false)} />
          <aside id="hlc-product-filters" ref={dialogRef} role={isModal && mobileFiltersOpen ? "dialog" : undefined} aria-hidden={isModal && !mobileFiltersOpen ? true : undefined} aria-modal={isModal && mobileFiltersOpen ? true : undefined} aria-label="Product filters" tabIndex={-1} inert={isModal && !mobileFiltersOpen ? true : undefined} className={`hlc-shop__filters${mobileFiltersOpen ? ' is-open' : ''}`}>
            {filterPanel}
          </aside>

          <main>
            {loading ? (
              <div className="hlc-shop__loading">Loading products…</div>
            ) : sortedProducts.length === 0 ? (
              <div className="hlc-empty-panel">
                <span className="hlc-empty-panel-icon"><HeadsetIcon /></span>
                <h2>No Products Yet</h2>
                <p>No products in this collection yet.</p>
                <button type="button" onClick={() => navigate('/shop')}>Explore All Lighting Solutions →</button>
              </div>
            ) : (
              <div className="hlc-shop__grid">
                {sortedProducts.map((product, idx) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    idx={idx}
                    cartItem={getCartItem(product.id)}
                    onAddToCart={(p) => dispatch(addToCart(p))}
                    onIncrement={(id) => dispatch(incrementQty(id))}
                    onDecrement={(id) => dispatch(decrementQty(id))}
                    onQuickView={openModal}
                    onView={viewProduct}
                  />
                ))}
              </div>
            )}
          </main>
        </div>

        <div className="hlc-shop__benefits">
          <div className="hlc-shop__benefits-inner">
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><ShieldIcon /></span>
              <div><h4>1-Year Warranty</h4><p>Peace of mind, always.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><EnergyLeafIcon /></span>
              <div><h4>Energy Efficient</h4><p>Save up to 85% electricity.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><TruckIcon /></span>
              <div><h4>Nationwide Delivery</h4><p>Fast &amp; reliable across Pakistan.</p></div>
            </div>
            <div className="hlc-shop__benefit">
              <span className="hlc-shop__benefit-icon"><HeadsetIcon /></span>
              <div><h4>Dedicated Support</h4><p>We're here to help you.</p></div>
            </div>
          </div>
        </div>
      </div>

      <HlcFooter />

      <QuickViewModal
        product={selectedProduct}
        open={modalOpen}
        onClose={closeModal}
        cartItem={selectedProduct ? getCartItem(selectedProduct.id) : null}
        onAddToCart={(p) => dispatch(addToCart(p))}
        onIncrement={(id) => dispatch(incrementQty(id))}
        onDecrement={(id) => dispatch(decrementQty(id))}
        onViewFull={viewProduct}
      />
    </>
  );
}
